<script>
  import { vec2 } from "gl-matrix";
  import { onMount, onDestroy } from "svelte";

  import frag from "./frag.glsl";
  import vert from "./vert.glsl";

  import { hexToVec3 } from "./lib/color.js";

  import { view } from "./stores/view.js";
  import {
    mode,
    seed,
    noiseFactor,
    reliefFactor,
    borderFactor,
    fakeHeightFactor,
    selectedOverlay,
    palette,
    overlays,
    canvasWidth,
    canvasHeight,
    waveStyle,
    waveOpacity,
    waveSpeed,
    waveScale,
    waveThickness,
    vignette,
    grain,
    resolutionScale,
  } from "./stores/globalControls.js";

  // fullscreen quad in clip space
  const QUAD_POSITIONS = new Float32Array([
    -1, -1, 0, 1, -1, 0, 1, 1, 0, -1, 1, 0,
  ]);
  const QUAD_UVS = new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]);
  const QUAD_INDICES = new Uint16Array([0, 1, 2, 2, 3, 0]);

  // Influences are uploaded as a (2N)x1 RGBA32F texture: two texels each,
  // (x, y, scale, factor) then (tintR, tintG, tintB, _). This decouples the
  // influence count from the shader, so there's no fixed limit and no recompile
  // when influences are added or removed.
  let influenceTexture = null;
  let influenceData = new Float32Array(8);

  let gl;
  let element;
  let program = null;
  let loc = {};
  let rafHandle = null;
  let startTime = null;
  let canvasDragging = false;
  let pixelRatio = 1;
  let webglUnsupported = false;

  $: paused = $mode === "help";

  $: paletteRGB = $palette.map(hexToVec3);
  $: topoInfluenceTransforms = $overlays
    .filter((o) => o.type === "topoInfluence")
    .map((o) => o.transform);

  $: topoInfluenceFactors = $overlays
    .filter((o) => o.type === "topoInfluence")
    .map((o) => o.factor);

  $: topoInfluenceColors = $overlays
    .filter((o) => o.type === "topoInfluence")
    .map((o) => hexToVec3(o.color || "#ffffff"));

  onMount(() => {
    gl = element.getContext("webgl2");
    if (!gl) {
      webglUnsupported = true;
      console.error("WebGL2 is not available in this browser.");
    }
  });

  function resetMode() {
    mode.set(null);
    selectedOverlay.set(null);
  }

  const MIN_FLING_SPEED = 0.3; // viewport-fractions/sec; flick faster than this to fling

  let previousPointerMoveEvent;
  const activePointers = new Map();
  let previousPinchDistance = null;
  let previousPinchMidpoint = null;
  const dragVelocity = vec2.create();
  let lastDragMoveTime = 0;

  function canvasStartDrag(e) {
    if (paused) {
      return;
    }
    activePointers.set(e.pointerId, e);
    if (activePointers.size === 1) {
      view.stop();
      vec2.set(dragVelocity, 0, 0);
      canvasDragging = true;
      selectedOverlay.set(null);
      previousPointerMoveEvent = e;
      addEventListener("pointermove", canvasDrag);
      addEventListener("pointerup", canvasStopDrag);
      addEventListener("pointercancel", canvasStopDrag);
    } else {
      previousPointerMoveEvent = null;
      previousPinchDistance = null;
      previousPinchMidpoint = null;
    }
  }

  function canvasDrag(e) {
    if (!activePointers.has(e.pointerId)) {
      return;
    }
    activePointers.set(e.pointerId, e);
    if (activePointers.size === 1) {
      if (previousPointerMoveEvent) {
        let delta = vec2.fromValues(
          (e.clientX - previousPointerMoveEvent.clientX) / $canvasWidth,
          (e.clientY - previousPointerMoveEvent.clientY) / $canvasHeight,
        );
        view.translate(vec2.negate(vec2.create(), delta));
        let dt = (e.timeStamp - previousPointerMoveEvent.timeStamp) / 1000;
        if (dt > 0) {
          let instant = vec2.scale(vec2.create(), delta, 1 / dt);
          vec2.lerp(dragVelocity, dragVelocity, instant, 0.5);
          lastDragMoveTime = e.timeStamp;
        }
      }
      previousPointerMoveEvent = e;
    } else if (activePointers.size === 2) {
      pinch();
    }
  }

  function pinch() {
    const [a, b] = [...activePointers.values()];
    const distance = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    const midpoint = vec2.fromValues(
      (a.clientX + b.clientX) / 2 / $canvasWidth,
      (a.clientY + b.clientY) / 2 / $canvasHeight,
    );
    if (previousPinchDistance !== null && distance > 0) {
      let delta = vec2.subtract(vec2.create(), midpoint, previousPinchMidpoint);
      view.translate(vec2.negate(vec2.create(), delta));
      view.zoom(previousPinchDistance / distance, midpoint);
    }
    previousPinchDistance = distance;
    previousPinchMidpoint = midpoint;
  }

  function canvasStopDrag(e) {
    if (!activePointers.has(e.pointerId)) {
      return;
    }
    activePointers.delete(e.pointerId);
    previousPinchDistance = null;
    previousPinchMidpoint = null;
    if (activePointers.size === 0) {
      canvasDragging = false;
      previousPointerMoveEvent = null;
      removeEventListener("pointermove", canvasDrag);
      removeEventListener("pointerup", canvasStopDrag);
      removeEventListener("pointercancel", canvasStopDrag);
      let fresh = e.timeStamp - lastDragMoveTime < 60;
      if (fresh && vec2.length(dragVelocity) > MIN_FLING_SPEED) {
        view.fling(dragVelocity);
      }
      vec2.set(dragVelocity, 0, 0);
    } else if (activePointers.size === 1) {
      previousPointerMoveEvent = [...activePointers.values()][0];
      vec2.set(dragVelocity, 0, 0);
    }
  }

  function canvasZoom(e) {
    let step = 0.1;
    let zoom = 1.0 + Math.sign(e.deltaY) * step;
    let center = vec2.fromValues(
      e.clientX / $canvasWidth,
      e.clientY / $canvasHeight,
    );
    view.smoothZoom(Math.log(zoom), center);
  }

  function resize() {
    if (gl) {
      const canvas = gl.canvas;
      const displayWidth = canvas.clientWidth;
      const displayHeight = canvas.clientHeight;

      pixelRatio = (window.devicePixelRatio || 1) * $resolutionScale;
      const bufferWidth = Math.round(displayWidth * pixelRatio);
      const bufferHeight = Math.round(displayHeight * pixelRatio);

      // Check if the drawing buffer is not the right size.
      if (canvas.width !== bufferWidth || canvas.height !== bufferHeight) {
        canvas.width = bufferWidth;
        canvas.height = bufferHeight;
        canvasWidth.set(displayWidth);
        canvasHeight.set(displayHeight);
        view.resize(displayWidth, displayHeight);
      }
      gl.viewport(0, 0, canvas.width, canvas.height);
    }
  }

  const UNIFORM_NAMES = [
    "time",
    "seed",
    "canvasTransform",
    "influenceData",
    "influenceCount",
    "pixelRatio",
    "noiseFactor",
    "reliefFactor",
    "borderFactor",
    "fakeHeightFactor",
    "waveStyle",
    "waveOpacity",
    "waveSpeed",
    "waveScale",
    "waveThickness",
    "vignette",
    "grain",
    "paletteDeepWater",
    "paletteShallowWater",
    "paletteShore",
    "paletteLand",
    "paletteMountain",
    "palettePeak",
  ];

  function compileShader(type, source) {
    const shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      console.error(gl.getShaderInfoLog(shader), source);
    }
    return shader;
  }

  function bindAttribute(data, name, size) {
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
    const location = gl.getAttribLocation(program, name);
    gl.enableVertexAttribArray(location);
    gl.vertexAttribPointer(location, size, gl.FLOAT, false, 0, 0);
  }

  function startGL() {
    if (!gl || program) {
      return;
    }

    program = gl.createProgram();
    gl.attachShader(program, compileShader(gl.VERTEX_SHADER, vert));
    gl.attachShader(program, compileShader(gl.FRAGMENT_SHADER, frag));
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error(gl.getProgramInfoLog(program));
    }
    gl.useProgram(program);

    bindAttribute(QUAD_POSITIONS, "position", 3);
    bindAttribute(QUAD_UVS, "uv", 2);
    const indexBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indexBuffer);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, QUAD_INDICES, gl.STATIC_DRAW);

    for (const name of UNIFORM_NAMES) {
      loc[name] = gl.getUniformLocation(program, name);
    }

    influenceTexture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, influenceTexture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    rafHandle = requestAnimationFrame(render);
  }

  function uploadInfluences() {
    const count = topoInfluenceTransforms.length;
    if (influenceData.length < count * 8) {
      influenceData = new Float32Array(count * 8);
    }
    for (let i = 0; i < count; i++) {
      const transform = topoInfluenceTransforms[i];
      const color = topoInfluenceColors[i];
      influenceData[i * 8 + 0] = transform[6]; // x
      influenceData[i * 8 + 1] = transform[7]; // y
      influenceData[i * 8 + 2] = transform[0]; // scale
      influenceData[i * 8 + 3] = topoInfluenceFactors[i]; // factor
      influenceData[i * 8 + 4] = color[0]; // tint r
      influenceData[i * 8 + 5] = color[1]; // tint g
      influenceData[i * 8 + 6] = color[2]; // tint b
      influenceData[i * 8 + 7] = 0.0; // unused
    }
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, influenceTexture);
    // width is clamped to at least 1 so the texture stays complete when empty;
    // influenceCount tells the shader how many influences are actually live.
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RGBA32F,
      Math.max(count * 2, 1),
      1,
      0,
      gl.RGBA,
      gl.FLOAT,
      influenceData,
    );
    gl.uniform1i(loc.influenceData, 0);
    gl.uniform1i(loc.influenceCount, count);
  }

  function render(now) {
    rafHandle = requestAnimationFrame(render);
    if (startTime === null) {
      startTime = now;
    }
    if (paused || paletteRGB.length < 6) {
      return;
    }
    view.advance(now);
    const time = (now - startTime) / 1000;

    gl.useProgram(program);
    gl.clearColor(0, 0, 0, 1);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

    gl.uniform1f(loc.time, time);
    gl.uniform1f(loc.seed, $seed);
    gl.uniformMatrix3fv(loc.canvasTransform, false, $view);
    gl.uniform1f(loc.pixelRatio, pixelRatio);
    gl.uniform1f(loc.noiseFactor, $noiseFactor);
    gl.uniform1f(loc.reliefFactor, $reliefFactor);
    gl.uniform1f(loc.borderFactor, $borderFactor);
    gl.uniform1f(loc.fakeHeightFactor, $fakeHeightFactor);
    gl.uniform1f(loc.waveStyle, $waveStyle);
    gl.uniform1f(loc.waveOpacity, $waveOpacity);
    gl.uniform1f(loc.waveSpeed, $waveSpeed);
    gl.uniform1f(loc.waveScale, $waveScale);
    gl.uniform1f(loc.waveThickness, $waveThickness);
    gl.uniform1f(loc.vignette, $vignette);
    gl.uniform1f(loc.grain, $grain);
    gl.uniform3fv(loc.paletteDeepWater, paletteRGB[0]);
    gl.uniform3fv(loc.paletteShallowWater, paletteRGB[1]);
    gl.uniform3fv(loc.paletteShore, paletteRGB[2]);
    gl.uniform3fv(loc.paletteLand, paletteRGB[3]);
    gl.uniform3fv(loc.paletteMountain, paletteRGB[4]);
    gl.uniform3fv(loc.palettePeak, paletteRGB[5]);

    uploadInfluences();

    gl.drawElements(gl.TRIANGLES, QUAD_INDICES.length, gl.UNSIGNED_SHORT, 0);
  }

  onDestroy(() => {
    if (rafHandle !== null) {
      cancelAnimationFrame(rafHandle);
    }
  });

  $: gl, resize(), startGL();
  $: $resolutionScale, resize();
</script>

<svelte:window on:resize={resize} />

<canvas
  bind:this={element}
  on:pointerdown={canvasStartDrag}
  on:wheel={canvasZoom}
  on:dblclick={resetMode}
  class:dragging={canvasDragging}
/>

{#if webglUnsupported}
  <div class="unsupported">
    This browser doesn't support WebGL2, which Mythic Atlas needs to render maps.
    Try a recent version of Chrome, Firefox, Safari, or Edge.
  </div>
{/if}

<style>
  canvas {
    display: block;
    width: 100%;
    height: 100%;
    user-select: none;
    -moz-user-select: none;
    -webkit-user-select: none;
  }
  canvas.dragging {
    cursor: grab;
  }
  .unsupported {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    max-width: 30em;
    padding: 1.5em;
    text-align: center;
    color: #eee;
    font-family: sans-serif;
    line-height: 1.5;
  }
</style>
